import type { Component } from "vue";
import type { RouteLocationRaw, RouteRecordRaw } from "vue-router";

export interface AutomationRoutesOptions {
  basePath: string
  containerComponent: Component | (() => Promise<Component>) | (() => Component)
  viewGlobal?: boolean
  defaultRedirect?: RouteLocationRaw
}

export function createAutomationRoutes(options: AutomationRoutesOptions): RouteRecordRaw[] {
  const {
    basePath,
    containerComponent,
    viewGlobal = false,
    defaultRedirect,
  } = options;

  const children: RouteRecordRaw[] = [
    {
      path: "topic-based/:dialog?",
      name: "automation-topic-based",
      component: () => import("./views/AutomationTopicOverview.vue"),
      props: () => ({ ...(viewGlobal ? { viewGlobal: true } : {}) }),
    },
    {
      path: "recurring-based/:dialog?",
      name: "automation-recurring-based",
      component: () => import("./views/AutomationRecurringOverview.vue"),
      props: () => ({ ...(viewGlobal ? { viewGlobal: true } : {}) }),
    },
    {
      path: "template/reservation/:dialog?",
      name: "automation-template-reservation",
      component: () => import("./views/AutomationTemplateReservationOverview.vue"),
      props: () => ({ ...(viewGlobal ? { viewGlobal: true } : {}) }),
    },
    {
      path: "template/recurring/:dialog?",
      name: "automation-template-recurring",
      component: () => import("./views/AutomationTemplateRecurringOverview.vue"),
      props: () => ({ ...(viewGlobal ? { viewGlobal: true } : {}) }),
    },
    {
      path: "template-instance/reservation",
      redirect: { name: "automation-template-instance-reservation" },
    },
    {
      path: "template-instance/reservation/tasks",
      name: "automation-template-instance-reservation",
      component: () => import("./views/AutomationTemplateInstanceReservationOverview.vue"),
      props: () => ({
        ...(viewGlobal ? { viewGlobal: true } : {}),
        overviewMode: "configured",
      }),
    },
    {
      path: "template-instance/reservation/templates",
      name: "automation-template-instance-reservation-templates",
      component: () => import("./views/AutomationTemplateInstanceReservationOverview.vue"),
      props: () => ({
        ...(viewGlobal ? { viewGlobal: true } : {}),
        overviewMode: "library",
      }),
    },
    {
      path: "template-instance/recurring",
      redirect: { name: "automation-template-instance-recurring" },
    },
    {
      path: "template-instance/recurring/tasks",
      name: "automation-template-instance-recurring",
      component: () => import("./views/AutomationTemplateInstanceRecurringOverview.vue"),
      props: () => ({
        ...(viewGlobal ? { viewGlobal: true } : {}),
        overviewMode: "configured",
      }),
    },
    {
      path: "template-instance/recurring/templates",
      name: "automation-template-instance-recurring-templates",
      component: () => import("./views/AutomationTemplateInstanceRecurringOverview.vue"),
      props: () => ({
        ...(viewGlobal ? { viewGlobal: true } : {}),
        overviewMode: "library",
      }),
    },
    {
      path: "date-rules/:dialog?",
      name: "automation-date-rules",
      component: () => import("./views/AutomationDateRulesOverview.vue"),
      props: () => (viewGlobal ? { viewGlobal: true } : {}),
    },
    {
      path: "/:catchAll(.*)",
      redirect: defaultRedirect || ({ name: "automation-topic-based" } as RouteLocationRaw),
    },
  ];

  const route: RouteRecordRaw = {
    path: basePath,
    name: "automations",
    component: containerComponent as RouteRecordRaw["component"],
    props: () => ({ ...(viewGlobal ? { viewGlobal: true } : {}) }),
    redirect: defaultRedirect || ({ name: "automation-topic-based" } as RouteLocationRaw),
    children,
  };

  return [route];
}

export function createCmsAutomationRoutes(containerComponent: () => Promise<unknown>): RouteRecordRaw[] {
  return createAutomationRoutes({
    basePath: "/automations",
    containerComponent,
    viewGlobal: true,
    defaultRedirect: { name: "automation-topic-based" },
  });
}

export function createWebAutomationRoutes(containerComponent: () => Promise<unknown>): RouteRecordRaw[] {
  return createAutomationRoutes({
    basePath: "/automations",
    containerComponent,
    defaultRedirect: { name: "automation-template-instance-reservation" },
  });
}
